/**
 * Chart of Accounts Management
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { BookOpen, Plus, Loader2, Edit2, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

const TYPE_CONFIG = {
  ASSET: { label: 'Asset', color: 'bg-blue-100 text-blue-700' },
  LIABILITY: { label: 'Liability', color: 'bg-red-100 text-red-700' },
  EQUITY: { label: 'Equity', color: 'bg-purple-100 text-purple-700' },
  INCOME: { label: 'Income', color: 'bg-green-100 text-green-700' },
  COGS: { label: 'COGS', color: 'bg-amber-100 text-amber-700' },
  EXPENSE: { label: 'Expense', color: 'bg-orange-100 text-orange-700' }
};

const TypeChip = ({ type }) => {
  const config = TYPE_CONFIG[type] || { label: type, color: 'bg-slate-100 text-slate-600' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
};

// Check if user can manage COA
const canManageCOA = (role) => ['super_admin', 'admin', 'finance', 'owner'].includes(role);

export default function ChartOfAccounts({ user }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: '', active_only: true });
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, account: null });
  const [formData, setFormData] = useState({ code: '', name: '', account_type: 'ASSET', description: '' });

  const canEdit = canManageCOA(user?.role);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.type) params.append('account_type', filters.type);
      params.append('active_only', filters.active_only);

      const response = await axios.get(`${API}/finance/hub/accounts?${params}`);
      setAccounts(response.data);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleCreate = async () => {
    if (!formData.code || !formData.name) {
      toast.error('Code and name are required');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        code: formData.code,
        name: formData.name,
        account_type: formData.account_type,
        description: formData.description || ''
      });
      
      await axios.post(`${API}/finance/hub/accounts?${params}`);
      toast.success('Account created');
      setCreateModal(false);
      setFormData({ code: '', name: '', account_type: 'ASSET', description: '' });
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    }
  };

  const handleUpdate = async () => {
    try {
      const params = new URLSearchParams();
      if (formData.name) params.append('name', formData.name);
      if (formData.description !== undefined) params.append('description', formData.description);
      
      await axios.patch(`${API}/finance/hub/accounts/${editModal.account.id}?${params}`);
      toast.success('Account updated');
      setEditModal({ open: false, account: null });
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update account');
    }
  };

  const handleToggleActive = async (account) => {
    if (account.is_system) {
      toast.error('Cannot modify system account');
      return;
    }
    
    try {
      const params = new URLSearchParams({ is_active: (!account.is_active).toString() });
      await axios.patch(`${API}/finance/hub/accounts/${account.id}?${params}`);
      toast.success(account.is_active ? 'Account disabled' : 'Account enabled');
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update account');
    }
  };

  const openEdit = (account) => {
    setFormData({ name: account.name, description: account.description || '' });
    setEditModal({ open: true, account });
  };

  return (
    <Card data-testid="chart-of-accounts">
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-indigo-500" />
          Chart of Accounts ({accounts.length})
        </CardTitle>
        {canEdit && (
          <Button size="sm" onClick={() => setCreateModal(true)}>
            <Plus className="h-4 w-4 mr-1" />
            New Account
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex gap-3 mb-4">
          <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="ASSET">Asset</SelectItem>
              <SelectItem value="LIABILITY">Liability</SelectItem>
              <SelectItem value="EQUITY">Equity</SelectItem>
              <SelectItem value="INCOME">Income</SelectItem>
              <SelectItem value="COGS">COGS</SelectItem>
              <SelectItem value="EXPENSE">Expense</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={filters.active_only ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilters({ ...filters, active_only: !filters.active_only })}
          >
            {filters.active_only ? 'Active Only' : 'Show All'}
          </Button>
          <Button variant="outline" onClick={fetchAccounts}>Refresh</Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : accounts.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  {canEdit && <TableHead className="w-[100px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map((acc) => (
                  <TableRow 
                    key={acc.id} 
                    className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 ${!acc.is_active ? 'opacity-50' : ''}`}
                  >
                    <TableCell className="font-mono font-medium">{acc.code}</TableCell>
                    <TableCell>
                      {acc.name}
                      {acc.is_system && <span className="ml-2 text-xs text-slate-400">(System)</span>}
                    </TableCell>
                    <TableCell><TypeChip type={acc.account_type} /></TableCell>
                    <TableCell className="text-right font-medium">
                      {acc.balance !== 0 ? formatCurrency(acc.balance) : '—'}
                    </TableCell>
                    <TableCell className="text-center">
                      {acc.is_active ? (
                        <span className="text-green-600 text-sm">Active</span>
                      ) : (
                        <span className="text-slate-400 text-sm">Inactive</span>
                      )}
                    </TableCell>
                    {canEdit && (
                      <TableCell>
                        <div className="flex gap-1">
                          {!acc.is_system && !acc.has_activity && (
                            <Button size="sm" variant="ghost" onClick={() => openEdit(acc)} title="Edit">
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {!acc.is_system && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleToggleActive(acc)}
                              title={acc.is_active ? 'Disable' : 'Enable'}
                            >
                              {acc.is_active ? (
                                <ToggleRight className="h-4 w-4 text-green-600" />
                              ) : (
                                <ToggleLeft className="h-4 w-4 text-slate-400" />
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">No accounts found</div>
        )}
      </CardContent>

      {/* Create Account Modal */}
      <Dialog open={createModal} onOpenChange={setCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Account Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="1500"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Account Type *</Label>
                <Select value={formData.account_type} onValueChange={(v) => setFormData({ ...formData, account_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ASSET">Asset</SelectItem>
                    <SelectItem value="LIABILITY">Liability</SelectItem>
                    <SelectItem value="EQUITY">Equity</SelectItem>
                    <SelectItem value="INCOME">Income</SelectItem>
                    <SelectItem value="COGS">COGS</SelectItem>
                    <SelectItem value="EXPENSE">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Account Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Prepaid Insurance"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Create Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Account Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, account: editModal.account })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Account: {editModal.account?.code}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label>Account Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            {editModal.account?.has_activity && (
              <p className="text-sm text-amber-600">
                This account has posted activity. Name changes are restricted.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, account: null })}>Cancel</Button>
            <Button onClick={handleUpdate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
